import { z } from 'zod';

export const REGEXES = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  website: /^https?:\/\/.+\..+/,
  fssai: /^\d{14}$/,
  gstin: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
};

export const BASE_SCHEMAS = {
  email: (fieldName: string = 'Email') =>
    z
      .string()
      .min(1, `${fieldName} is required`)
      .email('Please enter a valid email address'),

  optionalEmail: (fieldName: string = 'email') =>
    z
      .string()
      .refine(
        (val) => !val || val.trim() === '' || REGEXES.email.test(val.trim()),
        {
          message: `Please enter a valid ${fieldName}`,
        }
      )
      .optional(),

  password: (fieldName: string = 'Password', minLength: number = 6) =>
    z
      .string()
      .min(1, `${fieldName} is required`)
      .min(minLength, `${fieldName} must be at least ${minLength} characters`),

  website: z
    .string()
    .refine(
      (val) => !val || val.trim() === '' || REGEXES.website.test(val.trim()),
      {
        message: 'Please enter a valid website URL (e.g., https://example.com)',
      }
    ),

  fssai: z
    .string()
    .refine(
      (val) =>
        !val || val.trim() === '' || REGEXES.fssai.test(val.replace(/\s/g, '')),
      { message: 'FSSAI license number must be 14 digits' }
    ),

  gstin: z
    .string()
    .refine(
      (val) =>
        !val ||
        val.trim() === '' ||
        REGEXES.gstin.test(val.trim().toUpperCase()),
      { message: 'Please enter a valid GSTIN number' }
    ),

  phoneCode: z.string().min(1, 'Country code is required'),

  requiredString: ({
    fieldName,
    message,
    ...params
  }: {
    fieldName: string;
    message?: string;
    minLength?: number;
    maxLength?: number;
    length?: number;
  }) => {
    const { minLength, maxLength, length } = params;
    let schema = z.string().min(1, message || `${fieldName} is required`);

    if (length) {
      schema = schema.length(
        length,
        `${fieldName} must be exactly ${length} characters`
      );
    }
    if (minLength) {
      schema = schema.min(
        minLength,
        `${fieldName} must be at least ${minLength} characters long`
      );
    }
    if (maxLength) {
      schema = schema.max(
        maxLength,
        `${fieldName} must be less than ${maxLength} characters`
      );
    }
    return schema;
  },

  requiredNumericString: ({
    fieldName,
    ...params
  }: {
    fieldName: string;
    minLength?: number;
    maxLength?: number;
    length?: number;
  }) => {
    const { minLength, maxLength, length } = params;
    let schema = z
      .string()
      .min(1, `${fieldName} is required`)
      .regex(/^[0-9]+$/, {
        message: `${fieldName} must contain only digits`,
      });
    if (length) {
      schema = schema.length(
        length,
        `${fieldName} must be exactly ${length} digits`
      );
    }
    if (minLength) {
      schema = schema.min(
        minLength,
        `${fieldName} must be at least ${minLength} digits long`
      );
    }
    if (maxLength) {
      schema = schema.max(
        maxLength,
        `${fieldName} must be less than ${maxLength} digits`
      );
    }
    return schema;
  },

  requiredNumber: ({
    fieldName,
    params,
  }: {
    fieldName: string;
    params: { min?: number; max?: number };
  }) => {
    const { min, max } = params;
    let schema = z.number().min(1, `${fieldName} is required`);
    if (min && max) {
      schema = schema
        .min(min)
        .max(max, `${fieldName} must be between ${min} and ${max}`);
    }
    if (min) {
      schema = schema.min(
        min,
        `${fieldName} must be greater than or equal to ${min}`
      );
    }
    if (max) {
      schema = schema.max(
        max,
        `${fieldName} must be less than or equal to ${max}`
      );
    }
    return schema;
  },

  phoneNumber: (fieldName: string = 'Phone number') =>
    BASE_SCHEMAS.requiredNumericString({
      fieldName: fieldName,
      length: 10,
    }),

  optionalPhoneNumber: (fieldName: string = 'Phone number') =>
    z
      .string()
      .refine(
        (val) =>
          !val ||
          val.trim() === '' ||
          (val.length === 10 && /^[0-9]+$/.test(val)),
        { message: `${fieldName} must be 10 digits` }
      )
      .optional(),
};

export const SCHEMAS = {
  businessName: BASE_SCHEMAS.requiredString({
    fieldName: 'Business name',
    minLength: 2,
    maxLength: 40,
  }),
  businessLegalName: BASE_SCHEMAS.requiredString({
    fieldName: 'Business legal name',
    minLength: 2,
    maxLength: 60,
  }),
  businessType: BASE_SCHEMAS.requiredString({
    fieldName: 'Business type',
    message: 'Select a business type',
  }),
  businessEmail: BASE_SCHEMAS.optionalEmail('Business email'),
  businessPhoneCode: BASE_SCHEMAS.phoneCode.optional(),
  businessPhoneNumber: BASE_SCHEMAS.optionalPhoneNumber(),
  businessScale: BASE_SCHEMAS.requiredString({
    fieldName: 'Business scale',
    message: 'Select an option',
  }),
  businessGstinNumber: BASE_SCHEMAS.gstin.optional(),
  businessWebsite: BASE_SCHEMAS.website.optional(),
  businessDescription: BASE_SCHEMAS.requiredString({
    fieldName: 'Business description',
    minLength: 2,
    maxLength: 1000,
  }),

  outletName: BASE_SCHEMAS.requiredString({
    fieldName: 'Outlet name',
    minLength: 2,
    maxLength: 30,
  }),
  outletStreet: BASE_SCHEMAS.requiredString({
    fieldName: 'Street address',
    minLength: 2,
    maxLength: 100,
  }),
  outletCity: BASE_SCHEMAS.requiredString({
    fieldName: 'City',
    minLength: 2,
    maxLength: 20,
  }),
  outletState: BASE_SCHEMAS.requiredString({
    fieldName: 'State/Province',
    minLength: 2,
    maxLength: 20,
  }),
  outletCountry: BASE_SCHEMAS.requiredString({
    fieldName: 'Country code',
    length: 2,
  }),
  outletZipcode: BASE_SCHEMAS.requiredNumericString({
    fieldName: 'Pincode',
    length: 6,
  }),
  outletFssaiLicenseNumber: BASE_SCHEMAS.fssai.optional(),

  customerName: BASE_SCHEMAS.requiredString({
    fieldName: 'Name',
    minLength: 2,
    maxLength: 100,
  }),
  customerPhoneCode: BASE_SCHEMAS.phoneCode,
  customerPhoneNumber: BASE_SCHEMAS.phoneNumber(),

  userDisplayName: BASE_SCHEMAS.requiredString({
    fieldName: 'Display name',
    minLength: 2,
    maxLength: 40,
  }),

  authPhoneCode: BASE_SCHEMAS.phoneCode,
  authPhoneNumber: BASE_SCHEMAS.phoneNumber(),
};
